import BidAnalyticsIcon from "@/assets/images/bid-analytics-icon-dark-red.png";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEffect, useState } from "react";
import Card from "./common/Card";
import {
  useReadSpecCommodities,
  useUpdateSpecCommodities,
} from "@/libs/react-query/hooks/useSpecsQuery";
import {
  CommodityMatchResult,
  CommoditySpecInfo,
  CompanyCommodityInfo,
  ConfigurationMap,
} from "@/types/specs";
import {
  getMatchIcon,
  getStatusIcon,
  parsePrice,
  parseProjectType,
} from "../utils";
import { useParams } from "react-router-dom";
import { useSpecAnalysisStore } from "../stores/useSpecAnalysisStore";

// const TEMP_COMMODITY_PROJECT_ID = "1e066b55-ada8-11ef-b90a-0abfc5ac1701";

export default function CommoditySpecs() {
  const { projectId, projectType } = useParams();

  const [selectedEquipment, setSelectedEquipment] =
    useState<CommoditySpecInfo | null>(null);
  const [selectedItem, setSelectedItem] = useState<CompanyCommodityInfo | null>(
    null
  );
  const [selectedConfiguration, setSelectedConfiguration] =
    useState<ConfigurationMap | null>(null);

  const { setBudgetPrice, setSumPrice, setMatchRate } = useSpecAnalysisStore();

  // TODO: project_id 체크.
  const { data: commodityData } = useReadSpecCommodities({
    project_type: parseProjectType(projectType),
    project_id: projectId ?? "",
  });
  const { mutate: updateCommoditySelections } = useUpdateSpecCommodities({
    project_type: parseProjectType(projectType),
    project_id: projectId ?? "",
  });
  const { budget_amount = 0, commodities = [] } = commodityData || {};

  // 초기 배정예산 설정
  useEffect(() => {
    if (budget_amount) {
      setBudgetPrice(budget_amount);
    }
  }, [budget_amount]);

  // 초기 선택 장비 설정
  useEffect(() => {
    if (!commodities.length || selectedEquipment) return;

    const defaultEquipment = commodities[0];
    const selectedItemId = defaultEquipment.selected_company_commodity_id ?? "";
    const defaultItem =
      defaultEquipment.company_commodity_map[selectedItemId] ?? null;
    const defaultConfig = defaultItem
      ? (Object.values(defaultItem.configuration_map)?.[0] ?? null)
      : null;

    setSelectedEquipment(defaultEquipment);
    setSelectedItem(defaultItem);
    setSelectedConfiguration(defaultConfig);
  }, [commodities, selectedEquipment]);

  const handleSelectEquipment = (equipment: CommoditySpecInfo) => {
    setSelectedEquipment(equipment);
    const selectedItemId = equipment.selected_company_commodity_id ?? "";
    const defaultItem = equipment.company_commodity_map[selectedItemId] ?? null;
    setSelectedItem(defaultItem);
  };

  const handleSelectItem = (item: CompanyCommodityInfo) => {
    setSelectedItem(item);

    if (selectedEquipment) {
      updateCommoditySelections([
        {
          commodity_id: selectedEquipment.commodity_id,
          selected_company_commodity_id: item.commodity_id,
        },
      ]);
    }
  };

  useEffect(() => {
    if (selectedItem) {
      const firstConfig =
        Object.values(selectedItem.configuration_map)?.[0] ?? null;
      setSelectedConfiguration(firstConfig);
    }
  }, [selectedItem]);

  // 계산: 가격 총합 및 요구사항 일치율
  useEffect(() => {
    if (!commodities.length) return;

    let totalPrice = 0;
    let totalMatched = 0;
    let totalSpecs = 0;

    for (const commodity of commodities) {
      const selectedItemId = commodity.selected_company_commodity_id;
      if (!selectedItemId) continue;

      const selectedCompany = commodity.company_commodity_map[selectedItemId];
      if (!selectedCompany) continue;

      const price = selectedCompany.list_price ?? 0;
      const matched = selectedCompany.matched_spec_count ?? 0;
      const total = selectedCompany.total_spec_count ?? 0;

      totalPrice += parsePrice(price);
      totalMatched += matched;
      totalSpecs += total;
    }

    setSumPrice(totalPrice);
    const rate =
      totalSpecs > 0
        ? `${Math.round((totalMatched / totalSpecs) * 100).toFixed(0)}% (${totalMatched}/${totalSpecs})`
        : "-";

    setMatchRate(rate);
  }, [commodities]);

  return (
    <div className="grid grid-cols-[5.8fr,4.2fr] gap-5 mt-5 max-h-[1000px]">
      <Card icon={BidAnalyticsIcon} title="장비 분석">
        <div className="grid grid-cols-2 gap-x-[1px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-5 bg-dark-brown text-white text-xs font-semibold w-1/2">
                  규격서 요구사항
                </TableHead>
                <TableHead className="px-5 bg-dark-brown text-white text-xs font-semibold text-right w-1/2">
                  일치율 최상위, 자사제품
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commodities.map((commodity) => {
                const isSelected =
                  selectedEquipment?.commodity_id === commodity.commodity_id;
                const selectedCompanyItem =
                  commodity.company_commodity_map[
                    commodity.selected_company_commodity_id || ""
                  ];
                return (
                  <TableRow
                    key={commodity.commodity_id}
                    className="cursor-pointer"
                    onClick={() => handleSelectEquipment(commodity)}
                    data-state={isSelected ? "selected" : ""}
                  >
                    <TableCell className="px-5 text-sm font-normal">
                      {`${commodity.commodity_name} | ${commodity.quantity} ${commodity.unit}`}
                    </TableCell>
                    <TableCell className="px-5 text-sm font-normal text-right flex items-center justify-end gap-1">
                      {selectedCompanyItem && (
                        <>
                          <span className="text-xs font-semibold">
                            {`${selectedCompanyItem.matched_spec_count}/${selectedCompanyItem.total_spec_count}`}
                          </span>
                          <span className="h-5 max-w-32 px-3 bg-dark-red text-white text-[10px] font-bold rounded-2xl truncate">
                            {selectedCompanyItem.commodity_name}
                          </span>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-5 bg-dark-brown text-white text-xs font-semibold">
                  제품 정보
                </TableHead>
                <TableHead className="px-5 bg-dark-brown text-white text-xs font-semibold text-right whitespace-nowrap">
                  요구사항 일치 여부
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedEquipment &&
                Object.values(selectedEquipment.company_commodity_map).map(
                  (item) => (
                    <TableRow
                      key={item.commodity_id}
                      onClick={() => handleSelectItem(item)}
                      className="cursor-pointer"
                      data-state={
                        selectedItem?.commodity_id === item.commodity_id
                          ? "selected"
                          : ""
                      }
                    >
                      <TableCell className="px-5 text-sm font-normal">
                        {`${item.commodity_name} / ${item.sku_number || "-"} / ${parsePrice(item.list_price)?.toLocaleString() || "-"}`}
                      </TableCell>
                      <TableCell className="px-5 flex items-center justify-end gap-2">
                        <span className="text-sm font-normal">
                          {`${item.matched_spec_count}/${item.total_spec_count}`}
                        </span>
                        {getMatchIcon(
                          item.matched_spec_count,
                          item.total_spec_count
                        )}
                      </TableCell>
                    </TableRow>
                  )
                )}
            </TableBody>
          </Table>
        </div>
      </Card>
      <Card icon={BidAnalyticsIcon} title="규격서 요구사항" noBorder>
        <div className="flex flex-row">
          <div className="flex-[0.3]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-5 bg-dark-red text-white text-xs font-semibold">
                    구성
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedItem?.configuration_map &&
                  Object.values(selectedItem.configuration_map).map(
                    (config) => (
                      <TableRow
                        key={config.configuration}
                        onClick={() => setSelectedConfiguration(config)}
                        className="cursor-pointer"
                        data-state={
                          selectedConfiguration?.configuration ===
                          config.configuration
                            ? "selected"
                            : ""
                        }
                      >
                        <TableCell className="p-5 w-1/3 border-r border-b border-gray-D">
                          <p className="text-sm text-gray-A font-normal">
                            {config.configuration}
                          </p>
                          <div className="pt-3 flex justify-end gap-1">
                            <span className="text-xs text-gray-B font-semibold">
                              일치율
                            </span>
                            <span className="text-xs text-dark-brown font-semibold">
                              {!config.matched_spec_count ||
                              !config.total_spec_count
                                ? "-"
                                : Math.round(
                                    (config.matched_spec_count /
                                      config.total_spec_count) *
                                      100
                                  ) + "%"}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  )}
              </TableBody>
            </Table>
          </div>
          <div className="flex-[0.7]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-5 w-1/2 bg-dark-red text-white text-xs font-semibold">
                    제품 요구사항
                  </TableHead>
                  <TableHead className="px-5 w-1/2 bg-dark-red"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedConfiguration &&
                  Object.values(selectedConfiguration.specification_map).map(
                    (specification) => (
                      <TableRow
                        key={specification.project_specification}
                        className="border-b border-gray-D"
                      >
                        <TableCell className="px-5 py-2 flex items-center gap-1">
                          {getStatusIcon(
                            specification.match_result ===
                              CommodityMatchResult.MATCHED
                          )}
                          <span className="text-sm text-gray-A font-normal">
                            {specification.project_specification}
                          </span>
                        </TableCell>
                        <TableCell className="px-5 py-2 text-xs text-gray-A font-normal">
                          {specification.company_specification || "-"}
                        </TableCell>
                      </TableRow>
                    )
                  )}
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>
    </div>
  );
}
